"use client";

import React from "react";
import MotionHighlight from "@/components/effects/MotionHighlight";
import Flex from "@/components/layout/Flex";
import Button from "@/components/ui/Button";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { CarouselDotProps } from "../types";
import { useCarouselStore } from "./carousel-provider";

interface UseDotButtonType {
  selectedSlide: number;
  scrollSnapList: number[];
  onDotButtonClick: (index: number) => void;
}

export function useDotButton(): UseDotButtonType {
  const selectedSlide = useCarouselStore(store => store.selectedSlide);
  const scrollSnapList = useCarouselStore(store => store.scrollSnapList);
  const scrollTo = useCarouselStore(store => store.scrollTo);

  const onDotButtonClick = React.useCallback(
    (index: number) => {
      scrollTo(index);
    },
    [scrollTo],
  );

  return {
    onDotButtonClick,
    scrollSnapList,
    selectedSlide,
  };
}

function CarouselDots({ className, ...props }: CarouselDotProps) {
  const { selectedSlide, scrollSnapList, onDotButtonClick } = useDotButton();
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <Flex.Row {...native}>
      <MotionHighlight hover controlledItems>
        {scrollSnapList.map((_, index) => (
          <MotionHighlight.Item
            key={`${index * 2}`}
            activeClassName={cn(
              "rounded-full w-5 h-5 p-0",
              selectedSlide === index ? "bg-primary" : "bg-secondary",
            )}
          >
            <Button
              variant={selectedSlide === index ? "default" : "secondary"}
              className={cn("rounded-full w-5 h-5 p-0", className, classes)}
              onClick={() => onDotButtonClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          </MotionHighlight.Item>
        ))}
      </MotionHighlight>
    </Flex.Row>
  );
}

export default CarouselDots;
